import { Cell } from 'utils/data'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import type { TFunction } from 'i18next/typescript/t'
import { format, parseISO } from 'date-fns'
import { buildDataTableObject } from 'utils/data'
import { TableData } from 'utils/data'
import { GeneralizedComponentPayloadType } from 'components/generalized-component/generalized-component'
import { t } from 'i18next'
import { addHyperlinkToFirstColumn } from 'utils/data'
import { ITrailer } from './store-trailer-details/ITrailer'
import {
    ITrailerLocation,
    ScheduleEntry,
    Trailer,
    TrailerScheduleResponse,
} from '../../queries/trailer/trailer-schedule-response'

export const TRAILER_SUMMARY_PATH = '/trailer/summary'

export const RED_THRESHOLD_HOURS = 36

export const parseLeadTimeHours = (leadTime: string): number => {
    if (!leadTime) return 0
    const trimmed = leadTime.trim()
    const colonIndex = trimmed.indexOf(':')
    if (colonIndex !== -1) {
        return parseInt(trimmed.substring(0, colonIndex), 10) || 0
    }
    const spaceIndex = trimmed.indexOf(' ')
    if (spaceIndex !== -1) {
        return parseInt(trimmed.substring(0, spaceIndex), 10) || 0
    }
    return parseInt(trimmed, 10) || 0
}

export const isRedTrailer = (leadTime: string): boolean => {
    return parseLeadTimeHours(leadTime) >= RED_THRESHOLD_HOURS
}

export const createLeadTimeCell = (value: string): Cell => {
    if (isRedTrailer(value)) {
        return { value, className: 'hc8-clr-error' }
    }
    return { value }
}

export const getHeaders = (type: 'fdc' | 'rdc') => {
    const { trailer } = TRANSLATION_KEYS
    const trailerHeaders = trailer.schedule.headers
    return [
        { value: t(trailerHeaders.unloadDateTime) },
        { value: t(trailerHeaders.sequence) },
        { value: t(trailerHeaders.type) },
        { value: t(trailerHeaders.sweep) },
        { value: `${type.toUpperCase()} #` },
    ]
}
export const buildTrailerScheduleData = (
    data: TrailerScheduleResponse,
): GeneralizedComponentPayloadType => {
    const formatOrdinal = (n?: number | null): string => {
        if (!n) return ''
        const s = ['th', 'st', 'nd', 'rd'],
            v = n % 100
        return n + (s[(v - 20) % 10] || s[v] || s[0])
    }
    const columnIndexes = [
        'unload_time',
        'delivery_sequence',
        'unload_type',
        'sweep_eligible',
        'distribution_center_name',
    ] satisfies (keyof Trailer)[]

    const formatDate = (dateString?: string | null) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return format(date, 'EEE M/d h:mma')
    }

    const processData = (
        entries: ScheduleEntry[],
        type: 'fdc' | 'rdc',
    ): TableData => ({
        headings: getHeaders(type as 'fdc' | 'rdc'),
        rows: entries.flatMap((entry: ScheduleEntry) =>
            entry.trailers
                .filter(
                    (trailer: Trailer) =>
                        trailer.distribution_center_name !== 'C&S',
                )
                .map((trailer: Trailer) =>
                    columnIndexes.map((key) => ({
                        value:
                            key === 'sweep_eligible'
                                ? trailer[key]
                                    ? 'yes'
                                    : 'no'
                                : key === 'unload_time'
                                  ? formatDate(trailer[key])
                                  : key === 'delivery_sequence'
                                    ? formatOrdinal(trailer[key])
                                    : trailer[key] !== null &&
                                        trailer[key] !== undefined
                                      ? String(trailer[key])
                                      : '',
                    })),
                ),
        ),
    })

    const timeFrames = [
        'current_week',
        'last_week',
        'next_week',
        'three_weeks_out',
        'four_weeks_out',
    ] satisfies (keyof TrailerScheduleResponse)[]

    const { trailer } = TRANSLATION_KEYS
    const toggles = trailer.toggles

    const payload: GeneralizedComponentPayloadType = {
        [t(toggles.showRDC)]: timeFrames.map((timeFrame) => ({
            title: timeFrame.replace(/_/g, ' '),
            subtitle: '',
            data: {
                RDC: processData(data[timeFrame].rdc, 'rdc'),
            },
        })),
        [t(toggles.showFDC)]: timeFrames.map((timeFrame) => ({
            title: timeFrame.replace(/_/g, ' '),
            subtitle: '',
            data: {
                FDC: processData(data[timeFrame].fdc, 'fdc'),
            },
        })),
    }

    return payload
}

const {
    trailer: { rgd },
} = TRANSLATION_KEYS

const now = new Date()

export function formatDateTime(t: TFunction): string {
    const formattedDate = format(now, 'MMM d, yyyy h:mm a')
    return `${t(rgd.today)}, ${formattedDate}`
}

export const headings = [
    rgd.location,
    rgd.unacknowledged,
    rgd.red,
    rgd.recentlyAcknowledged,
]

export const formattedHeadings: Cell[] = headings.map((heading) => ({
    value: heading,
}))

export const createRow = (location: ITrailerLocation): Cell[] => [
    { value: `${location.location_id}` },
    { value: `${location.unacknowledged_trailers}` },
    { value: `${location.unacknowledged_trailers_red_status}` },
    { value: `${location.acknowledged_trailers}` },
]

type ExtractedShipment = {
    type: string
    trailer_id: string
    move_id: string
    cartons: number
    acknowledged?: string
    closed?: string
    lead_time?: string
}

export const getTrailerSummaryTimestamp = (timestamp: string) => {
    return format(parseISO(timestamp), 'M/d h:mm a')
}

export const extractTrailerSummaryData = (shipments: ITrailer[]) => {
    if (!Array.isArray(shipments)) return []

    return shipments.map((shipment) => {
        if (!shipment) return [{}]

        const {
            type,
            acknowledged_timestamp,
            shipment_closed_timestamp,
            trailer_id,
            cartons,
            lead_time,
            move_id,
        } = shipment

        return acknowledged_timestamp
            ? {
                  type,
                  acknowledged: getTrailerSummaryTimestamp(
                      acknowledged_timestamp,
                  ).toLowerCase(),
                  trailer_id,
                  move_id,
                  cartons,
              }
            : {
                  type,
                  closed: format(
                      parseISO(shipment_closed_timestamp),
                      'M/d h:mm a',
                  ).toLowerCase(),
                  trailer_id,
                  move_id,
                  cartons,
                  lead_time,
              }
    })
}

export const transformShipmentsForTable = (
    shipments: ITrailer[],
    currentLocation: string,
) => {
    const extractedData = extractTrailerSummaryData(shipments)

    if (!extractedData.length) return null

    const hasAcknowledged = extractedData.some((item) => 'acknowledged' in item)

    const { trailer } = TRANSLATION_KEYS
    const tableHeaders = trailer.details.table

    const columnMappings: { [key: string]: keyof ExtractedShipment } = {
        [t(tableHeaders.type)]: 'type',
        [t(tableHeaders.acknowledged)]: 'acknowledged',
        [t(tableHeaders.closed)]: 'closed',
        [t(tableHeaders.trailerNumber)]: 'trailer_id',
        [t(tableHeaders.cartons)]: 'cartons',
        [t(tableHeaders.leadTime)]: 'lead_time',
    }

    const headings = hasAcknowledged
        ? [
              t(tableHeaders.type),
              t(tableHeaders.acknowledged),
              t(tableHeaders.trailerNumber),
              t(tableHeaders.cartons),
          ]
        : [
              t(tableHeaders.type),
              t(tableHeaders.closed),
              t(tableHeaders.trailerNumber),
              t(tableHeaders.cartons),
              t(tableHeaders.leadTime),
          ]

    const formattedData = {
        headings: headings.map((value) => ({ value })),
        rows: extractedData.map((item) => {
            const typedItem = item as ExtractedShipment
            return headings.map((heading) => {
                const key = columnMappings[heading] as keyof ExtractedShipment
                if (key === 'trailer_id') {
                    return {
                        value: String(typedItem[key] ?? ''),
                        is_link: true,
                        url: `/trailer/detail?locationId=${currentLocation}&moveId=${typedItem['move_id']}`,
                    }
                }
                if (key === 'lead_time') {
                    return createLeadTimeCell(String(typedItem[key] ?? ''))
                }
                return { value: String(typedItem[key] ?? '') }
            })
        }),
    }

    return buildDataTableObject({
        currentData: formattedData,
        widths: Array(headings.length).fill(40),
        handleClick: () => {},
        pinFirstColumn: false,
        sortable: false,
        isLoading: false,
        onSort: () => {},
    })
}

export function buildLinkedRows({
    childRows,
    grandchildRows,
    headings,
    currentLocation,
    pathname,
}: {
    childRows: Cell[][]
    grandchildRows: Cell[][]
    headings: Cell[]
    currentLocation: string
    pathname: string
}): Cell[][] {
    const childData: TableData = { headings, rows: childRows }
    const grandchildData: TableData = { headings, rows: grandchildRows }
    const processedChild = addHyperlinkToFirstColumn(
        childData,
        currentLocation,
        pathname,
    )
    const processedGrandchild = addHyperlinkToFirstColumn(
        grandchildData,
        currentLocation,
        TRAILER_SUMMARY_PATH,
    )
    return [...processedChild.rows, ...processedGrandchild.rows]
}
